import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import {
  AnyOffshoreSectionState,
  FuelRobState,
  OffshoreStockState,
} from '@navstation-portal/reporting/data-model/offshore-report';
import {
  INIT_MODES,
  InitMode,
  StockTankFormState,
  StockTanks,
  TankTransferFormState,
  TankTransfersState,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { VesselSpecStore } from '@navstation-portal/vessels/data-access/store';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import { initStateField } from '../../../offshore-report-utils/store.utility';
import { createDynamicSectionState } from '../../offshore-vm-builder-helpers';
import { withOffshoreFormStoreReducer } from '../../with-offshore-form-store-reducer';
import { buildOffshoreStockVm } from './offshore-stock-vm-builders';
import { offshoreStockFormEvents } from './with-offshore-stock-events';

export function buildStockState(data?: OffshoreStockState) {
  return {
    options: {
      tankSounding: initStateField<boolean>(),
    },

    tankTransfers: {
      ...tankTransfersArray(data?.tankTransfers),
    },
    fuelRob: {
      ...fuelRobArray(data?.fuelRob),
    },
    fuelWaterRob: {
      bilgeWater: initStateField<number>(),
      dischargedSludge: initStateField<number>(),
      dischargedBilgeWater: initStateField<number>(),
      freshWater: initStateField<number>(),
      distilledWater: initStateField<number>(),
      tankCleaningChemical: initStateField<number>(),
    },

    lubeOilRob: {
      lubeOilSystem: initStateField<number>(),
      lubeOilSpare: initStateField<number>(),
      lowBnCylinderOil: initStateField<number>(),
      highBnCylinderOil: initStateField<number>(),
      aeLubeOil: initStateField<number>(),
      turboOil: initStateField<number>(),
      hydraulicOil: initStateField<number>(),
      otherOil: initStateField<number>(),
    },

    oilLosses: {
      totalWasteOil: initStateField<number>(),
      incineratedWasteOil: initStateField<number>(),
      evaporatedWaterFromWasteOil: initStateField<number>(),
      sludgeRob: initStateField<number>(),
      dischargedWasteOil: initStateField<number>(),
    },
    tanks: {
      ...tankReportingArray(data?.tanks),
    },
    dynamicSection: createDynamicSectionState([]),
  };
}

const tankTransfersArray = (data?: TankTransfersState) => {
  return {
    transfers: data ? generateTankTransfersArray(data) : [],
  };
};

const fuelRobArray = (data?: FuelRobState) => {
  return {
    fuelRobRows: generateFuelRobRows(data),
  };
};

const generateFuelRobRows = (data?: FuelRobState) => {
  if (!data?.fuelRobRows?.length) {
    return Array.from({ length: 4 }, () => ({
      fuelType: initStateField<SelectOption>(),
      quantity: initStateField<number>(),
    }));
  }

  return data.fuelRobRows.map((row) => ({
    fuelType: initStateField<SelectOption>(
      row.fuelType
        ? {
            name: row.fuelType.toUpperCase(),
            code: row.fuelType,
          }
        : undefined
    ),
    quantity: initStateField<number>(row.quantity),
  }));
};

const initBlank = () => buildStockState();

const initWithCarryOver = (data: OffshoreStockState) => {
  return buildStockState(data);
};

const initWithExistingReport = (data: OffshoreStockState) => {
  return buildStockState(data);
};

const generateTankTransfersArray = (data: TankTransfersState) => {
  const result: TankTransferFormState[] = data.map(() => ({
    from: initStateField<SelectOption | undefined>(),
    to: initStateField<SelectOption | undefined>(),
    quantity: initStateField<number | undefined>(),
  }));
  return result;
};

const tankReportingArray = (data?: StockTanks | undefined) => {
  if (!data)
    return {
      tankRows: [] as StockTankFormState[],
    };

  return {
    tankRows: generateTankReportingArray(data),
  };
};

const generateTankReportingArray = (data: StockTanks) => {
  if (!data) return [];
  const result: StockTankFormState[] = Object.entries(data).map(([key, tank]) => ({
    id: initStateField<string | undefined>(key),
    _id: initStateField<string | undefined>(tank?._id?.$oid),
    amount: initStateField<number | undefined>(tank?.amount),
    amountBeforeBunkering: initStateField<number | undefined>(tank?.amountBeforeBunkering),
    bdnNumber: initStateField<string | undefined>(tank?.bdnNumber),
    bunkerDate: initStateField<Date | undefined>(
      tank?.bunkerDate ? new Date(tank.bunkerDate as unknown as string) : undefined
    ),
    bunkerPort: initStateField<string | undefined>(tank?.bunkerPort),
    density: initStateField<number | undefined>(tank?.density),
    densityBeforeBunkering: initStateField<number | undefined>(tank?.densityBeforeBunkering),
    fuelClass: initStateField<string | undefined>(tank?.fuelClass),
    fuelGrade: initStateField<SelectOption | undefined>(tank?.fuelGrade),
    fuelGradeAddedInTank: initStateField<SelectOption | undefined>(tank?.fuelGradeAddedInTank),
    groupColor: initStateField<string | undefined>(undefined),
    index: initStateField<number | undefined>(undefined),
    inUse: initStateField<boolean | undefined>(tank?.inUse),
    isBlend: initStateField<boolean | undefined>(tank?.isBlend),
    lcv: initStateField<number | undefined>(tank?.lcv),
    lcvBeforeBunkering: initStateField<number | undefined>(tank?.lcvBeforeBunkering),
    stableUntil: initStateField<Date | undefined>(
      tank?.stableUntil ? new Date(tank.stableUntil as unknown as string) : undefined
    ),
    sulphurBeforeBunkering: initStateField<number | undefined>(tank?.sulphurBeforeBunkering),
    sulphurAfterBunkering: initStateField<number | undefined>(tank?.sulphurAfterBunkering),
    tankCapacity: initStateField<number | undefined>(),
    tankName: initStateField<string | undefined>(),
    quantityAddedInTank: initStateField<number | undefined>(tank?.quantityAddedInTank),
    viscosity: initStateField<number | undefined>(tank?.viscosity),
  }));

  return result;
};

const initOffshoreStockStateReducer = (initialData: AnyOffshoreSectionState, initMode: InitMode) => {
  const data = initialData as OffshoreStockState;
  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver(data);
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data);
    case INIT_MODES.BLANK:
      return initBlank();
    default:
      return initBlank();
  }
};

export const OffshoreStockFormStore = signalStore(
  { providedIn: 'root' },
  withDevtools('OffshoreStockFormStore'),
  withState(initBlank()),
  withOffshoreFormStoreReducer(offshoreStockFormEvents, initOffshoreStockStateReducer, initBlank),
  withComputed((store) => {
    const vesselSpecStore = inject(VesselSpecStore);

    return {
      vm: computed(() => buildOffshoreStockVm(store, vesselSpecStore.vesselSpec())),
    };
  })
);
